'use client';

import { type ReactNode, type Ref } from 'react';
import { Package, Receipt } from 'lucide-react';
import { formatCurrency } from '@/lib/formatters/australian-formatters';
import { EmptyState } from '@/components/shared/empty-state';
import { useListRowRhythm } from '@/components/shared/list-row';
import { cn } from '@/lib/utils';
import type { InventoryItem } from '@/lib/services/inventory-items/types';
import {
  InventoryItemRow,
  type InventoryRowStruckParts
} from './inventory-item-row';
import { DraggableInventoryItemRow } from './draggable-inventory-item-row';
import {
  SectionHeader,
  SectionTotal,
  EmptySection
} from './section-components';

/**
 * One group's card (VALE-1494). `overflow-hidden` so the grey heading strip at
 * its top takes the rounded corners instead of squaring them off.
 */
/**
 * **The card one review list sits on** — and both of them do (VALE-1617).
 *
 * `overflow-hidden` is the load-bearing word. The grey header strip is a
 * square-cornered fill, so without it the strip's corners sit outside the
 * card's radius and the header reads as un-rounded while the card below it is
 * rounded. The contacts list carried `bg-card rounded-xl` hand-written and was
 * missing exactly that, so the two halves of one review disagreed about their
 * own shape.
 *
 * Exported so the contacts half binds to it rather than restating two of its
 * three classes.
 */
export const GROUP_CARD = 'bg-card overflow-hidden rounded-xl';

interface BalanceSheetSectionProps {
  sectionLabel: string;
  assets: InventoryItem[];
  liabilities: InventoryItem[];
  assetsTotal: number;
  liabilitiesTotal: number;
  onItemClick?: (item: InventoryItem) => void;
  isDraggable?: boolean;
  isDropTarget?: boolean;
  isDraggedOver?: boolean;
  dropRef?: Ref<HTMLDivElement>;
  /**
   * Have a group with no rows **account for itself** with the standard
   * `EmptyState`, rather than fall back to the one-line placeholder row
   * (VALE-1494).
   *
   * This replaces VALE-977's `hideEmptyGroups`, which dropped an empty group
   * entirely — heading, placeholder, total and all. That was argued as "an AI
   * extract is not a balance sheet, so four headings over two rows is chrome",
   * and round 5 of the review's design **reversed it**: a group that vanishes
   * leaves the reader unable to tell "the documents mentioned no liabilities"
   * from "this review does not cover liabilities", and the sections either side
   * of the gap close up so the remaining ones run together.
   *
   * An empty group under this flag shows no total either, for the reason the
   * old flag had: a `$0.00` total is a claim about rows the group does not
   * have. Defaults to `false`, leaving the matter Inventory tab on its
   * `No estate liabilities` placeholder plus its total, unchanged.
   */
  emptyGroupsStateThemselves?: boolean;
  /**
   * **Which groups this surface covers at all** (VALE-1654). Defaults to both.
   *
   * Distinct from a group being EMPTY, and the distinction is the one
   * {@link BalanceSheetSectionProps.emptyGroupsStateThemselves} exists to
   * protect: an empty group states itself precisely so the reader can tell "the
   * documents mentioned no liabilities" from "this surface does not cover
   * liabilities". Some surfaces genuinely are the second, and for them an empty
   * group is not reassurance, it is a list that can never fill.
   *
   * Discovery's institution panel is the case: an institution there is
   * asset-side or liability-side, by `isLiabilityInstitution`, and never both —
   * a share registry cannot hold a mortgage, so `No estate liabilities` under a
   * share registry's assets states the obvious forever.
   */
  groups?: ReadonlyArray<'asset' | 'liability'>;
  /**
   * **Something that belongs at the top of a group, above its rows**
   * (VALE-1654) — today, the card for an item being added.
   *
   * A sibling of `sectionActions`, which puts the *control* on the group
   * heading: this is where what that control opens then appears. Below the
   * rows it would be a long way from the button that opened it on a populated
   * list, and outside the group entirely it stops reading as part of it.
   */
  groupLeading?: { asset?: ReactNode; liability?: ReactNode };
  /**
   * **Scroll the ROWS, not the group** (VALE-1654).
   *
   * The group becomes a column that fills the height it is given: its header
   * and its add card stay put, and only the rows beneath them scroll, so the
   * scrollbar belongs to the list rather than to the panel around it.
   *
   * Tried first as a sticky header, which is the same picture by a different
   * mechanism and a worse one: the header still lives inside the scroller, so
   * it rides the scrollbar's full track and overlaps the first row on the way.
   * Taking it out of the scrolling box is the honest version.
   *
   * Opt-in, and only meaningful where the surface gives the group a bounded
   * height. Discovery's institution panel does; the Inventory tab scrolls the
   * page instead, where this would trap four lists in their own little boxes.
   */
  scrollRows?: boolean;
  /**
   * Draw an empty group's state, instead of the default `EmptyState` this
   * section builds (VALE-1657).
   *
   * The AI review passes it so its inventory groups draw through the same
   * module as its people, entitlements and documents lists — one definition of
   * what an empty list on that surface looks like, which is the whole of what
   * the ticket asked for. A render prop rather than an import from the feature
   * folder, matching `renderGroupLegend` and friends: the balance sheet is a
   * generic component and must not reach into the one surface that styles it.
   *
   * Only consulted while {@link BalanceSheetSectionProps.emptyGroupsStateThemselves}
   * is on — with it off the group falls back to the one-line placeholder row,
   * which the Inventory tab still uses.
   *
   * **Returning `null` means the group draws nothing** (VALE-1737), and a group
   * that draws nothing AND carries no Add is omitted whole — heading, card and
   * all. That is the AI review's empty-list rule reaching the inventory groups;
   * `ai-review/compact/review-empty-state.tsx` owns it and the reasoning. The
   * default `EmptyState` below is unreachable for a caller that passes this
   * prop, so the two cannot disagree about what an empty group looks like.
   */
  renderEmptyGroup?: (
    sectionLabel: string,
    noun: 'assets' | 'liabilities'
  ) => ReactNode;
  /**
   * Give each group (Assets, Liabilities) **its own card**, a gap apart
   * (VALE-1494).
   *
   * **Four treatments were tried at sign-off and the first three all lost to
   * the same thing, which is why the history is worth keeping.** Plain space
   * between the groups read as whitespace belonging to the list ABOVE it.
   * Closing each group with a rule and then leaving the gap fixed that and was
   * rejected on sight, as too much air on a surface that already runs long. A
   * hairline on the next heading's top edge with no space at all was accepted,
   * then rejected once seen in place: **every one of those three leaves the
   * groups sharing one card, and inside one card the space between them is the
   * card's own background, so the eye reads straight through and four groups
   * scan as one long list.** The division has to be the card boundary itself,
   * which is exactly what makes the contacts block and the inventory block read
   * as separate things on this surface.
   *
   * Defaults to `false`, so the matter Inventory tab's continuous table is
   * unchanged: that surface IS one table, and one card is the truth about it.
   */
  groupsAsCards?: boolean;

  /** Trailing per-row actions (the `⋯` menu). See `BalanceSheet`. */
  renderRowActions?: (item: InventoryItem) => ReactNode;
  /** Whether this table states figures at all (VALE-1363). See `BalanceSheet`. */
  showValues?: boolean;
  /** Whether rows state their lifecycle status (VALE-1584). See `BalanceSheet`. */
  showStatus?: boolean;
  /** The Add control for this group, rendered at the right of its heading. */
  assetActions?: ReactNode;
  liabilityActions?: ReactNode;
  /** An indented detail block under each row (VALE-1181). See `BalanceSheet`. */
  renderExpanded?: (item: InventoryItem) => ReactNode;
  /** A badge after the row's own information (VALE-1181). See `BalanceSheet`. */
  renderItemBadge?: (item: InventoryItem) => ReactNode;
  /** Classes for a whole item, row and block (VALE-1181). See `BalanceSheet`. */
  itemClassName?: (item: InventoryItem) => string | undefined;
  /**
   * A small node beside a group heading's own title — the AI review's change
   * legend, keyed to **that group's own rows** (VALE-1494).
   *
   * Beside the title rather than in the far-right slot, which is where
   * `ContactList.labelSuffix` puts the same node and for the same reason: the
   * right edge is shared with the value column, so a legend there reads as a
   * column heading rather than as a key to the list.
   *
   * **A function of the group's rows, not one node for the whole card**
   * (VALE-1494). VALE-1181 put a single legend on the Estate Assets heading
   * and argued that repeating it over all four "would state one fact four
   * times" — true of a card-wide key, and the reason it was right then. It is
   * not a card-wide key any more: each heading now answers "what did the AI do
   * to the rows *under this heading*", so a group holding only creates says
   * `NEW` and the group above it holding an update says `UPDATED`. A group
   * whose rows the AI did not touch renders nothing, which is what stops this
   * becoming the repetition VALE-1181 rejected.
   */
  renderGroupLegend?: (items: InventoryItem[]) => ReactNode;
  /** Wrap a row's own line, never its block (VALE-1181). See `BalanceSheet`. */
  renderRowWrapper?: (item: InventoryItem, row: ReactNode) => ReactNode;
  /** Which parts of a row line read as rejected (VALE-1181). See `BalanceSheet`. */
  struckParts?: (item: InventoryItem) => InventoryRowStruckParts;
}

export function BalanceSheetSection({
  sectionLabel,
  assets,
  liabilities,
  assetsTotal,
  liabilitiesTotal,
  onItemClick,
  isDraggable,
  isDropTarget,
  isDraggedOver,
  dropRef,
  emptyGroupsStateThemselves = false,
  groups = ['asset', 'liability'],
  groupLeading,
  scrollRows = false,
  renderEmptyGroup,
  groupsAsCards = false,
  renderRowActions,
  showValues = true,
  showStatus = true,
  assetActions,
  liabilityActions,
  renderExpanded,
  renderItemBadge,
  itemClassName,
  renderGroupLegend,
  renderRowWrapper,
  struckParts
}: BalanceSheetSectionProps) {
  const lowerLabel = sectionLabel.toLowerCase();
  /*
    **The column label goes with the column** (VALE-1363). A heading that says
    `DOD Value` over rows that state no figure names a column that is not there,
    so `showValues` governs both or neither.

    It travels as `rightLabel`, which is `SectionHeader`'s own single-value slot.
    VALE-977's `actions` route existed to carry TWO labels plus a reserved `⋯`
    gutter so each sat over the figures it named; with one column and one label
    there is nothing to align, and with none there is nothing to say.
  */
  const columnLabel = (dodLabel: string) => (showValues ? dodLabel : undefined);

  /*
    **Every group renders, on both treatments.** VALE-977 hid an empty group
    and VALE-1078 then had to carve out "unless it carries an Add control",
    because the control lives in the heading and hiding the group made adding
    the first asset unreachable. VALE-1494 removed the hiding outright, which
    removes the carve-out with it: there is no longer a state in which a group
    the reviewer can add to is missing, because no group is ever missing.
  */
  const assetsEmpty = assets.length === 0;
  const liabilitiesEmpty = liabilities.length === 0;
  /*
    A total is a claim about rows the group does not have, so an empty group
    closes with nothing. Unchanged reasoning from the flag this replaces; it
    now applies to the group that STATES itself rather than the one kept for
    its Add control alone.
  */
  const suppressAssetsTotal = emptyGroupsStateThemselves && assetsEmpty;
  const suppressLiabilitiesTotal =
    emptyGroupsStateThemselves && liabilitiesEmpty;

  const ItemRow = isDraggable ? DraggableInventoryItemRow : InventoryItemRow;

  /*
    **A heading's legend and its Add control are two slots, not one**
    (VALE-1617). They were composed into a single title-side span, so moving
    the Add to the free right edge took the legend with it — and the legend is
    the one mark that must NOT go there: right-aligned it reads as a column
    label rather than as a key to the rows beneath (VALE-1494's own round).

    `SectionHeader` owns where each one lands; this only says which is which.
  */
  const groupLegend = (items: InventoryItem[]) => renderGroupLegend?.(items);
  /* The rule between rows, from the enclosing surface's rhythm (VALE-1494). */
  const { divider } = useListRowRhythm();

  /*
    One group's empty rendering, so Assets and Liabilities cannot state
    themselves differently. `EmptyState` is the standard primitive
    (`.claude/docs/standardised-components.md`); the placeholder row is what
    the Inventory tab still uses, and the two are deliberately not blended.
  */
  const emptyGroup = (noun: 'assets' | 'liabilities') => {
    if (!emptyGroupsStateThemselves) {
      return <EmptySection message={`No ${lowerLabel} ${noun}`} />;
    }
    /*
      **The surface's own treatment when it has one** (VALE-1657). The AI review
      holds one definition of an empty list across all seven of its lists, and a
      copy of it built here would be the eighth. Everything else keeps the
      dense, description-less state VALE-1494's sign-off settled on: four groups
      stack in one card and three of them can be empty at once, so a sentence
      restating the heading two lines above made three of them read as a wall of
      furniture.
    */
    /*
      `??` used to sit here, which made `null` from the surface indistinguishable
      from no surface renderer at all and fell through to the default tile —
      the exact thing VALE-1737 asks an empty list not to draw.
    */
    if (renderEmptyGroup) return renderEmptyGroup(sectionLabel, noun);
    return (
      <EmptyState
        dense
        icon={noun === 'assets' ? Package : Receipt}
        title={`No ${lowerLabel} ${noun}`}
      />
    );
  };

  /*
    **An empty group that draws nothing and offers nothing is omitted whole**
    (VALE-1737) — its card and its heading go with its rows.

    The Add is what decides it. An empty group the reviewer can still put an
    asset into is a list that is named and actionable, so its heading earns its
    place; one with neither rows nor an Add is a heading over nothing, and on an
    extract that found little the review becomes a column of those. See
    `ai-review/compact/review-empty-state.tsx`.
  */
  const groupIsOmitted = (
    empty: boolean,
    noun: 'assets' | 'liabilities',
    actions: ReactNode,
    /*
      What the group holds above its rows (VALE-1654) — today the card for an
      item being added. A group drawing one is not empty, whatever its row
      count says, and omitting it would take the half-filled form the reviewer
      is typing into off the screen.
    */
    leading: ReactNode
  ) =>
    empty &&
    emptyGroupsStateThemselves &&
    !actions &&
    !leading &&
    emptyGroup(noun) == null;
  const omitAssets = groupIsOmitted(
    assetsEmpty,
    'assets',
    assetActions,
    groupLeading?.asset
  );
  const omitLiabilities = groupIsOmitted(
    liabilitiesEmpty,
    'liabilities',
    liabilityActions,
    groupLeading?.liability
  );

  /*
    **A section whose every group is omitted draws nothing either**
    (VALE-1949). Its wrapper would otherwise survive with no children, and the
    AI review's `gap-10` stack spaces around it: an estate section with nothing
    in it leaves a stray band above the non-estate rows.
  */
  const drawsAssets = groups.includes('asset') && !omitAssets;
  const drawsLiabilities = groups.includes('liability') && !omitLiabilities;
  if (!drawsAssets && !drawsLiabilities) return null;

  /*
    One group's rows, so Assets and Liabilities cannot render a row two ways.
    Extracted with the empty rendering above when VALE-1494 gave each group a
    second empty branch: the two blocks were already near-identical, and a
    third variant apiece was how they would have started to diverge.
  */
  const rows = (items: InventoryItem[]) => (
    <div
      className={cn(
        'divide-y',
        divider,
        /*
          **The scroller, and it shrinks to its content** — see `scrollRows`.

          `flex-1` here first, which made it GROW into whatever height was
          going: a list of three rows stretched to the bottom of the column and
          the last one read as an enormous card with a sea of white under it.
          A flex child shrinks by default, so `min-h-0` alone is the whole rule
          — short content keeps its natural height, long content is capped by
          the column and scrolls.
        */
        scrollRows && 'min-h-0 overflow-y-auto'
      )}
    >
      {items.map((item) => (
        <ItemRow
          key={item.id}
          item={item}
          onClick={onItemClick}
          trailing={renderRowActions?.(item)}
          showValue={showValues}
          showStatus={showStatus}
          expanded={renderExpanded?.(item)}
          infoBadge={renderItemBadge?.(item)}
          itemClassName={itemClassName?.(item)}
          rowWrapper={
            renderRowWrapper ? (row) => renderRowWrapper(item, row) : undefined
          }
          struck={struckParts?.(item)}
        />
      ))}
    </div>
  );

  return (
    <div
      ref={dropRef}
      className={cn(
        'rounded-lg transition-all duration-150',
        /*
          **Part of the height chain** (VALE-1654). `scrollRows` makes the rows
          the scroller, and a scroller only has a height if every ancestor
          between it and the bounded box passes one down. This element sat
          outside that chain, so the group grew to fit its rows and nothing
          scrolled anywhere — no scrollbar, and the list simply ran past the
          bottom of the column.
        */
        scrollRows && 'flex min-h-0 flex-col',
        /* The section holds the gap; each group below holds a card. */
        groupsAsCards && 'flex flex-col gap-10',
        isDropTarget &&
          !isDraggedOver &&
          'ring-dashed bg-info-subtle ring-info/40 ring-2',
        isDraggedOver && 'ring-dashed bg-info-subtle ring-info ring-2'
      )}
    >
      {/* Assets */}
      {drawsAssets && (
        <div
          className={cn(
            groupsAsCards && GROUP_CARD,
            /* The group fills its column so its rows can scroll inside it. */
            scrollRows && 'flex min-h-0 flex-col'
          )}
        >
          <SectionHeader
            title={`${sectionLabel} Assets`}
            rightLabel={columnLabel('DOD Value')}
            titleAction={groupLegend(assets)}
            endAction={assetActions}
          />
          {groupLeading?.asset}
          {assetsEmpty && !groupLeading?.asset
            ? emptyGroup('assets')
            : rows(assets)}
          {/*
          **The total is the value column's footer, so it goes with the
          column** (VALE-1363). On the AI-extract review it summed PROPOSALS,
          stating what the estate was worth before the reviewer had accepted
          anything — and going wrong the moment an item or a field was turned
          down.
        */}
          {showValues && !suppressAssetsTotal && (
            <SectionTotal
              label={`Total ${sectionLabel} Assets`}
              total={assetsTotal}
            />
          )}
        </div>
      )}

      {/* Liabilities */}
      {drawsLiabilities && (
        <div
          className={cn(
            groupsAsCards && GROUP_CARD,
            scrollRows && 'flex min-h-0 flex-col'
          )}
        >
          <SectionHeader
            title={`${sectionLabel} Liabilities`}
            rightLabel={columnLabel('DOD Balance')}
            titleAction={groupLegend(liabilities)}
            endAction={liabilityActions}
          />
          {groupLeading?.liability}
          {liabilitiesEmpty && !groupLeading?.liability
            ? emptyGroup('liabilities')
            : rows(liabilities)}
          {/* See the Assets total above. */}
          {showValues && !suppressLiabilitiesTotal && (
            <SectionTotal
              label={`Total ${sectionLabel} Liabilities`}
              total={liabilitiesTotal}
            />
          )}
        </div>
      )}

      {/*
        Net row — only meaningful when both sides are on screen WITH their
        totals, so an empty group that closes with no total takes the net line
        with it: netting against it would state a figure nothing above it
        supports.

        And a table that states no figures has no net (VALE-1363): this row is
        the difference of the two totals above it, so it cannot outlive them.
      */}
      {showValues && !suppressAssetsTotal && !suppressLiabilitiesTotal && (
        <div className="border-border-subtle flex items-center justify-between border-t-2 px-4 py-3">
          <span className="text-foreground text-sm font-bold">
            Net {sectionLabel}
          </span>
          {/*
            Hand-rolled rather than a `SectionTotal` because this row is a
            difference and not a sum. It right-aligns to the container, which
            is where the one value column's own totals sit.
          */}
          <span className="text-foreground text-sm font-bold tabular-nums">
            {formatCurrency(assetsTotal - liabilitiesTotal)}
          </span>
        </div>
      )}
    </div>
  );
}
